import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { teamService } from '../../services/teamService';
import { ssoService } from '../../services/ssoService';
import NavigationSidebar from '../../components/NavigationSidebar';
import SeatAllocationCard from './components/SeatAllocationCard';
import TeamMemberActivity from './components/TeamMemberActivity';

import AgentAdoptionChart from './components/AgentAdoptionChart';
import InviteMemberModal from './components/InviteMemberModal';
import SSOStatusCard from './components/SSOStatusCard';
import ComplianceDashboard from '../team-management/components/ComplianceDashboard';
import { Users, TrendingUp, Activity, UserPlus, Shield } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [seatInfo, setSeatInfo] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [ssoStatus, setSsoStatus] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (user?.id) {
      loadTeams();
    }
  }, [user]);

  useEffect(() => {
    if (selectedTeam?.team_id) {
      loadTeamData();
      loadSSOStatus();
    }
  }, [selectedTeam]);

  const loadTeams = async () => {
    setLoading(true);
    try {
      const { data, error } = await teamService?.getUserTeams(user?.id);
      if (!error && data) {
        setTeams(data);
        // Select first team with admin/owner role
        const adminTeam = data?.find(t => t?.member_role === 'owner' || t?.member_role === 'admin');
        if (adminTeam) {
          setSelectedTeam(adminTeam);
        }
      }
    } catch (error) {
      console.error('Error loading teams:', error);
      toast?.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamData = async () => {
    try {
      const [membersResult, seatResult, statsResult] = await Promise.all([
        teamService?.getTeamMembers(selectedTeam?.team_id),
        teamService?.checkSeatAvailability(selectedTeam?.team_id),
        teamService?.getTeamUsageStats(selectedTeam?.team_id)
      ]);

      if (!membersResult?.error) setTeamMembers(membersResult?.data || []);
      if (!seatResult?.error) setSeatInfo(seatResult?.data);
      if (!statsResult?.error) setUsageStats(statsResult?.data);
    } catch (error) {
      console.error('Error loading team data:', error);
    }
  };

  const loadSSOStatus = async () => {
    try {
      const { data, error } = await ssoService?.getProviderStatus(selectedTeam?.organization_id);
      if (!error && data) {
        setSsoStatus(data);
      }
    } catch (error) {
      console.error('Error loading SSO status:', error);
    }
  };

  const handleInviteMember = async (email, role) => {
    try {
      const { error } = await teamService?.createTeamInvitation(
        selectedTeam?.team_id,
        email,
        role,
        user?.id
      );

      if (error) throw error;

      toast?.success('Invitation sent successfully');
      setShowInviteModal(false);
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast?.error(error?.message || 'Failed to send invitation');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!confirm('Are you sure you want to remove this member?')) return;

    try {
      const { error } = await teamService?.removeMember(selectedTeam?.team_id, memberId);
      if (error) throw error;

      toast?.success('Member removed successfully');
      loadTeamData();
    } catch (error) {
      console.error('Error removing member:', error);
      toast?.error('Failed to remove member');
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      const { error } = await teamService?.updateMemberRole(selectedTeam?.team_id, memberId, newRole);
      if (error) throw error;

      toast?.success('Role updated successfully');
      loadTeamData();
    } catch (error) {
      console.error('Error updating role:', error);
      toast?.error('Failed to update role');
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <NavigationSidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading admin dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedTeam) {
    return (
      <div className="flex h-screen bg-gray-50">
        <NavigationSidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Admin Access</h2>
            <p className="text-gray-600 mb-6">You need admin or owner role to access this dashboard</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Toaster position="top-right" />
      <NavigationSidebar />
      <div className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center p-2">
                  <img 
                    src="/assets/images/Photo_Feb_21_2026_12_00_04_AM_1_-1771799585955.png" 
                    alt="DraftIQ Logo" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
                  <p className="text-gray-600">Manage team members, seats, and monitor usage</p>
                </div>
              </div>
              
              {/* Team Selector */}
              {teams?.length > 1 && (
                <select
                  value={selectedTeam?.team_id}
                  onChange={(e) => {
                    const team = teams?.find(t => t?.team_id === e?.target?.value);
                    setSelectedTeam(team);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {teams?.map((team) => (
                    <option key={team?.team_id} value={team?.team_id}>
                      {team?.team_name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Current Team Info */}
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg p-6 mb-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-1">{selectedTeam?.team_name}</h2>
                <p className="text-purple-100">{selectedTeam?.organization_name}</p>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-sm font-medium text-gray-600">Total Members</h3>
              </div>
              <p className="text-3xl font-bold text-gray-900">{teamMembers?.length || 0}</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="text-sm font-medium text-gray-600">Total Interactions</h3>
              </div>
              <p className="text-3xl font-bold text-gray-900">{usageStats?.totalInteractions || 0}</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-sm font-medium text-gray-600">Tasks Completed</h3>
              </div>
              <p className="text-3xl font-bold text-gray-900">{usageStats?.totalTasks || 0}</p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-3 mb-8">
            <button
              onClick={() => setShowInviteModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
            >
              <UserPlus className="w-5 h-5" />
              Invite Member
            </button>
            <button
              onClick={() => navigate('/sso-settings')}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              <Shield className="w-5 h-5" />
              SSO Settings
            </button>
          </div>

          {/* Navigation Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <div className="flex gap-4">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'overview' ?'border-purple-600 text-purple-600' :'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('compliance')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'compliance' ?'border-purple-600 text-purple-600' :'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Compliance Dashboard
              </button>
            </div>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              {/* Dashboard Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <SeatAllocationCard seatInfo={seatInfo} teamTier={selectedTeam?.tier} />
                <SSOStatusCard ssoStatus={ssoStatus} organizationId={selectedTeam?.organization_id} />
              </div>

              {/* Agent Adoption */}
              <AgentAdoptionChart stats={usageStats} />

              {/* Team Members */}
              <TeamMemberActivity 
                members={teamMembers} 
                onRemoveMember={handleRemoveMember}
                onUpdateRole={handleUpdateRole}
                currentUserId={user?.id}
              />
            </>
          )}

          {/* Compliance Dashboard Tab */}
          {activeTab === 'compliance' && (
            <ComplianceDashboard organizationId={selectedTeam?.organization_id} />
          )}
        </div>
      </div>
      {/* Invite Member Modal */}
      {showInviteModal && (
        <InviteMemberModal
          onClose={() => setShowInviteModal(false)}
          onInvite={handleInviteMember}
        />
      )}
    </div>
  );
};

export default AdminDashboard;